
import { useEffect, useState } from 'react'
import MessageBox from '../components/MessageBox'
import EditButton from '../components/EditButtonModal'
import { fetchWithAuth } from '../utils/fetchWithAuth'

const LABELS = {
  FLOATING_PROMO_BANNER_VISIBLE_MS:
    'Floating banner - doba zobrazenia',

  FLOATING_PROMO_BANNER_REOPEN_MS:
    'Floating banner - opätovné otvorenie',

  ACCESS_TOKEN_TTL:
    'Access token TTL',

  REFRESH_TOKEN_YEARS:
    'Refresh token - platnosť',

  RESET_TOKEN_TTL_MINUTES:
    'Reset token TTL',

  VAT_ENABLED:
    'Zobrazovať ceny s DPH',

  FOUNDING_MEMBERSHIP_LIMIT:
    'Limit zvýhodnených členstiev'
}

const PARAMETER_CONFIG = {
  FLOATING_PROMO_BANNER_VISIBLE_MS: {
    unit: 'sekúnd',
    toDisplay: value =>
      String(Number(value) / 1000),
    toStorage: value =>
      String(Number(value) * 1000)
  },

  FLOATING_PROMO_BANNER_REOPEN_MS: {
    unit: 'minút',
    toDisplay: value =>
      String(Number(value) / 60000),
    toStorage: value =>
      String(Number(value) * 60000)
  },

  ACCESS_TOKEN_TTL: {
    unit: 'minút',
    toDisplay: value => {
      const raw = String(value ?? '').trim()

      if (raw.endsWith('m')) {
        return raw.slice(0, -1)
      }

      if (raw.endsWith('h')) {
        const hours = Number(
          raw.slice(0, -1)
        )

        return Number.isFinite(hours)
          ? String(hours * 60)
          : raw
      }

      return raw
    },
    toStorage: value =>
      `${Number(value)}m`
  },

  REFRESH_TOKEN_YEARS: {
    unit: 'rokov',
    toDisplay: value =>
      String(value ?? ''),
    toStorage: value =>
      String(Number(value))
  },

  RESET_TOKEN_TTL_MINUTES: {
    unit: 'minút',
    toDisplay: value =>
      String(value ?? ''),
    toStorage: value =>
      String(Number(value))
  }
}

const AdminSystemParameters = () => {
  const [parameters, setParameters] =
    useState([])

  const [loading, setLoading] =
    useState(true)

  const [message, setMessage] =
    useState(null)

  const [editKey, setEditKey] =
    useState(null)

  const [editValue, setEditValue] =
    useState('')

  const [savingKey, setSavingKey] =
    useState(null)

  const loadParameters = async () => {
    setLoading(true)

    try {
      const res = await fetchWithAuth(
        '/admin/system-parameters',
        {
          cacheTtlMs: 0
        }
      )

      const data = await res.json()

      if (!res.ok) {
        throw new Error(
          data?.message ||
            'Nepodarilo sa načítať systémové parametre'
        )
      }

      setParameters(
        Array.isArray(data?.parameters)
          ? data.parameters
          : []
      )
    } catch (err) {
      setMessage({
        type: 'error',
        message:
          err.message ||
          'Nepodarilo sa načítať systémové parametre'
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadParameters()
  }, [])

  const handleEdit = (parameter) => {
    const config =
      PARAMETER_CONFIG[parameter.key]

    setEditKey(parameter.key)

    setEditValue(
      config
        ? config.toDisplay(
            parameter.value
          )
        : String(
            parameter.value ?? ''
          )
    )

    setMessage(null)
  }

  const handleCancel = () => {
    setEditKey(null)
    setEditValue('')
  }

  const handleSave = async (key) => {
    const numericValue =
      Number(editValue)

    if (
      !Number.isFinite(numericValue) ||
      numericValue <= 0
    ) {
      setMessage({
        type: 'error',
        message:
          'Hodnota musí byť kladné číslo'
      })

      return
    }

    setSavingKey(key)
    setMessage(null)

    try {
      const config =
        PARAMETER_CONFIG[key]

      const storageValue =
        config
          ? config.toStorage(
              editValue
            )
          : editValue

      const res = await fetchWithAuth(
        `/admin/system-parameters/${encodeURIComponent(
          key
        )}`,
        {
          method: 'PUT',

          headers: {
            'Content-Type':
              'application/json'
          },

          body: JSON.stringify({
            value: storageValue
          })
        }
      )

      const data = await res.json()

      if (!res.ok) {
        throw new Error(
          data?.message ||
            'Parameter sa nepodarilo uložiť'
        )
      }

      setEditKey(null)
      setEditValue('')

      await loadParameters()

      setMessage({
        type: 'success',
        message:
          'Systémový parameter bol uložený'
      })
    } catch (err) {
      setMessage({
        type: 'error',
        message:
          err.message ||
          'Parameter sa nepodarilo uložiť'
      })
    } finally {
      setSavingKey(null)
    }
  }

  const handleVatToggle = async (parameter) => {
    if (savingKey !== null) return

    const newValue =
      parameter.value === 'true'
        ? 'false'
        : 'true'

    setSavingKey(parameter.key)
    setMessage(null)

    try {
      const res = await fetchWithAuth(
        '/admin/system-parameters/VAT_ENABLED',
        {
          method: 'PUT',

          headers: {
            'Content-Type':
              'application/json'
          },

          body: JSON.stringify({
            value: newValue
          })
        }
      )

      const data = await res.json()

      if (!res.ok) {
        throw new Error(
          data?.message ||
            'Nepodarilo sa zmeniť nastavenie DPH'
        )
      }

      setParameters(prev =>
        prev.map(item =>
          item.key === 'VAT_ENABLED'
            ? {
                ...item,
                value: newValue
              }
            : item
        )
      )
    } catch (err) {
      setMessage({
        type: 'error',
        message:
          err.message ||
          'Nepodarilo sa zmeniť nastavenie DPH'
      })
    } finally {
      setSavingKey(null)
    }
  }

  const getDisplayValue = (
    parameter
  ) => {
    const config =
      PARAMETER_CONFIG[
        parameter.key
      ]

    if (!config) {
      return String(
        parameter.value ?? ''
      )
    }

    return config.toDisplay(
      parameter.value
    )
  }

  const getUnit = (key) => {
    return (
      PARAMETER_CONFIG[key]?.unit ||
      ''
    )
  }

  return (
    <div style={styles.wrapper}>
      <div style={styles.headerRow}>
        <div>
          <h3 style={styles.title}>
            Systémové parametre
          </h3>

          <p style={styles.subtitle}>
            Nastavenie globálnych
            parametrov systému.
          </p>
        </div>
      </div>

      {message && (
        <MessageBox
          message={message.message}
          type={message.type}
        />
      )}

      {loading ? (
        <p>Načítavam...</p>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr
                style={
                  styles.headerRowTable
                }
              >
                <th style={styles.th}>
                  Akcie
                </th>

                <th style={styles.th}>
                  Parameter
                </th>

                <th style={styles.th}>
                  Hodnota
                </th>

                <th style={styles.th}>
                  Popis
                </th>
              </tr>
            </thead>

            <tbody>
              {parameters.map(
                parameter => {
                  const isEditing =
                    editKey ===
                    parameter.key

                  const unit =
                    getUnit(
                      parameter.key
                    )

                  return (
                    <tr
                      key={
                        parameter.key
                      }
                    >
                      <td
                        style={
                          styles.td
                        }
                      >
                        {parameter.key === 'VAT_ENABLED' ? null : isEditing ? (
                          <>
                            <button
                              type="button"
                              disabled={
                                savingKey ===
                                parameter.key
                              }
                              onClick={() =>
                                handleSave(
                                  parameter.key
                                )
                              }
                            >
                              Uložiť
                            </button>

                            <button
                              type="button"
                              disabled={
                                savingKey ===
                                parameter.key
                              }
                              onClick={
                                handleCancel
                              }
                              style={{
                                marginLeft: 8
                              }}
                            >
                              Zrušiť
                            </button>
                          </>
                        ) : (
                          <EditButton
                            onClick={() =>
                              handleEdit(
                                parameter
                              )
                            }
                          />
                        )}
                      </td>

                      <td
                        style={
                          styles.td
                        }
                      >
                        <strong>
                          {LABELS[
                            parameter.key
                          ] ||
                            parameter.key}
                        </strong>

                        <div
                          style={
                            styles.parameterKey
                          }
                        >
                          {
                            parameter.key
                          }
                        </div>
                      </td>

                      <td
                        style={
                          styles.td
                        }
                      >
                        {parameter.key === 'VAT_ENABLED' ? (
                          <label
                            style={
                              styles.toggleLabel
                            }
                          >
                            <input
                              type="checkbox"
                              role="switch"
                              aria-label="Zobrazovať ceny s DPH"
                              checked={
                                parameter.value === 'true'
                              }
                              disabled={
                                savingKey !== null
                              }
                              onChange={() =>
                                handleVatToggle(
                                  parameter
                                )
                              }
                              style={
                                styles.toggleInput
                              }
                            />

                            <span
                              style={{
                                ...styles.toggleTrack,
                                backgroundColor:
                                  parameter.value === 'true'
                                    ? '#107c6b'
                                    : '#999'
                              }}
                            >
                              <span
                                style={{
                                  ...styles.toggleThumb,
                                  transform:
                                    parameter.value === 'true'
                                      ? 'translateX(22px)'
                                      : 'translateX(0)'
                                }}
                              />
                            </span>

                            <span>
                              {parameter.value === 'true'
                                ? 'Zapnuté'
                                : 'Vypnuté'}
                            </span>
                          </label>
                        ) : isEditing ? (
                          <div
                            style={
                              styles.valueEditor
                            }
                          >
                            <input
                              type="number"
                              min="1"
                              step="1"
                              value={
                                editValue
                              }
                              onChange={e =>
                                setEditValue(
                                  e.target.value
                                )
                              }
                              style={
                                styles.input
                              }
                            />

                            {unit && (
                              <span
                                style={
                                  styles.unit
                                }
                              >
                                {unit}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div
                            style={
                              styles.valueDisplay
                            }
                          >
                            <span>
                              {getDisplayValue(
                                parameter
                              )}
                            </span>

                            {unit && (
                              <span
                                style={
                                  styles.unitRead
                                }
                              >
                                {unit}
                              </span>
                            )}
                          </div>
                        )}
                      </td>

                      <td
                        style={
                          styles.td
                        }
                      >
                        {parameter.description ||
                          '-'}
                      </td>
                    </tr>
                  )
                }
              )}

              {!parameters.length && (
                <tr>
                  <td
                    colSpan={4}
                    style={
                      styles.emptyTd
                    }
                  >
                    Žiadne systémové
                    parametre.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

const styles = {
  wrapper: {
    background: '#ededed',
    padding: 12,
    border:
      '1.5px solid #107c6b',
    borderRadius: 10,
    width: '100%',
    boxSizing: 'border-box',
    position: 'relative',
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column'
  },

  headerRow: {
    display: 'flex',
    justifyContent:
      'space-between',
    alignItems: 'flex-start',
    gap: 16,
    marginBottom: 16
  },

  title: {
    fontSize: 18,
    marginTop: 0,
    marginBottom: 0
  },

  subtitle: {
    margin: '6px 0 0',
    color: '#555'
  },

  tableWrap: {
    border:
      '1px solid #c7c7c7',
    borderRadius: 10,
    background: '#fff',
    flex: 1,
    minHeight: 0,
    maxWidth: '100%',
    overflow: 'auto'
  },

  table: {
    width: '100%',
    borderCollapse:
      'collapse',
    backgroundColor: '#fff'
  },

  headerRowTable: {
    background: '#c8e1e1'
  },

  th: {
    textAlign: 'left',
    padding: '8px 12px',
    fontWeight: 700,
    fontSize: 13,
    whiteSpace: 'nowrap'
  },

  td: {
    borderTop:
      '1px solid #ececec',
    padding: '10px 12px',
    fontSize: 13
  },

  input: {
    width: 100,
    boxSizing: 'border-box'
  },

  valueEditor: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },

  valueDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: 5
  },

  unit: {
    fontSize: 13,
    color: '#555',
    whiteSpace: 'nowrap'
  },

  unitRead: {
    color: '#555'
  },

  parameterKey: {
    marginTop: 3,
    fontSize: 11,
    color: '#777'
  },

  emptyTd: {
    padding: 18,
    textAlign: 'center',
    color: '#666'
  },

  toggleLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
    position: 'relative'
  },

  toggleInput: {
    position: 'absolute',
    opacity: 0,
    width: 48,
    height: 26,
    margin: 0,
    cursor: 'pointer',
    zIndex: 1
  },

  toggleTrack: {
    display: 'inline-flex',
    alignItems: 'center',
    width: 48,
    height: 26,
    padding: 2,
    borderRadius: 20,
    boxSizing: 'border-box',
    transition:
      'background-color 0.2s'
  },

  toggleThumb: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    backgroundColor: '#fff',
    boxShadow:
      '0 1px 3px rgba(0,0,0,0.25)',
    transition:
      'transform 0.2s'
  }
}

export default AdminSystemParameters
